import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const TYPE_COLOUR = {
  annual: "bg-primary/70",
  sick: "bg-amber-500/70",
  unpaid: "bg-red-500/60",
  casual: "bg-sky-500/70",
  maternity: "bg-fuchsia-500/60",
  paternity: "bg-fuchsia-500/50",
  compassionate: "bg-teal-500/60",
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const shiftMonth = (month, by) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (month) => {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

export const LeaveCalendar = ({ initialMonth }) => {
  const [month, setMonth] = useState(initialMonth);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      const res = await api.get(`/hr/leave/calendar/${month}`);
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => {
    if (!data) return [];
    const lead = data.days.length ? data.days[0].weekday : 0;
    return [...Array(lead).fill(null), ...data.days];
  }, [data]);

  return (
    <Panel
      testId="panel-leave-calendar"
      title="Who is away"
      kicker={data ? `${data.scope === "everyone" ? "Everyone" : "My team"} · busiest day has ${data.totals.busiest_day} away` : "Loading"}
      actions={(
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 w-8 rounded-full p-0"
                  data-testid="calendar-prev" onClick={() => setMonth(shiftMonth(month, -1))}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="min-w-[130px] text-center text-[12.5px] font-medium text-foreground"
                data-testid="calendar-month">{monthLabel(month)}</span>
          <Button size="sm" variant="outline" className="h-8 w-8 rounded-full p-0"
                  data-testid="calendar-next" onClick={() => setMonth(shiftMonth(month, 1))}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    >
      {!data ? (
        <div className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the calendar
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1.5">
            {DOW.map((d) => (
              <div key={d} className="pb-1 text-[10px] uppercase tracking-[1.2px] text-muted-foreground">
                {d}
              </div>
            ))}
            {cells.map((day, i) => day === null ? <div key={`pad-${i}`} /> : (
              <div key={day.date} data-testid={`calendar-day-${day.date}`}
                   className={`min-h-[92px] rounded-xl border p-2 transition-colors duration-150 ${
                     day.working_day ? "border-border bg-card" : "border-border/60 bg-muted/30"}`}>
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {day.date.slice(-2)}
                  </span>
                  {day.headcount_away > 0 && (
                    <span data-testid={`calendar-count-${day.date}`}
                          className="rounded-full bg-primary/10 px-1.5 font-mono text-[10px] text-primary">
                      {day.headcount_away}
                    </span>
                  )}
                </div>
                {day.holiday && (
                  <div className="mt-1 text-[10px] text-muted-foreground">{day.holiday}</div>
                )}
                <div className="mt-1 space-y-1">
                  {day.away.slice(0, 3).map((p) => (
                    <div key={p.leave_ref}
                         title={`${p.name} · ${p.leave_label} · ${p.status.replace(/_/g, " ")}`}
                         className={`truncate rounded px-1.5 py-0.5 text-[10.5px] text-white ${
                           TYPE_COLOUR[p.leave_type] || "bg-muted-foreground/60"} ${
                           p.confirmed ? "" : "opacity-50"}`}>
                      {p.name.split(" ")[0]}
                    </div>
                  ))}
                  {day.away.length > 3 && (
                    <div className="text-[10px] text-muted-foreground">
                      +{day.away.length - 3} more
                    </div>
                  )}
                </div>
                {(day.manager_clashes.length > 0 || day.team_clashes.length > 0) && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"
                       data-testid={`calendar-clash-${day.date}`}>
                    <TriangleAlert className="h-3 w-3" />
                    {day.manager_clashes.length > 0 ? "same manager" : "same role"}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {Object.entries(TYPE_COLOUR).map(([code, colour]) => (
              <span key={code} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className={`h-2.5 w-2.5 rounded-full ${colour}`} /> {code}
              </span>
            ))}
            <span className="ml-2 text-[11px] text-muted-foreground">
              faded means it is still awaiting a decision
            </span>
          </div>
        </>
      )}
    </Panel>
  );
};

export const ClashNote = ({ leaveRef }) => {
  const [clash, setClash] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/hr/leave/clash/${leaveRef}`)
      .then((res) => { if (live) setClash(res.data); })
      .catch(() => {});
    return () => { live = false; };
  }, [leaveRef]);

  if (!clash) return null;
  if (clash.headcount_away === 0) {
    return (
      <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-[12px] text-emerald-700 dark:text-emerald-300"
         data-testid="clash-none">
        Nobody else is booked off over these dates.
      </p>
    );
  }
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3" data-testid="clash-note">
      <p className="text-[12px] font-medium text-amber-700 dark:text-amber-300">
        {clash.headcount_away} other {clash.headcount_away === 1 ? "person is" : "people are"} away
        over these dates
        {clash.same_manager > 0 ? ` · ${clash.same_manager} report to the same manager` : ""}
        {clash.same_team > 0 ? ` · ${clash.same_team} do the same job` : ""}
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {clash.overlaps.slice(0, 5).map((o) => (
          <li key={o.leave_ref} className="text-[11.5px] text-muted-foreground">
            {o.name} · {o.from_date} → {o.to_date} · {o.status.replace(/_/g, " ")}
            {o.same_manager && (
              <Chip className="ml-1.5 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                same manager
              </Chip>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
